
import { useMemo } from "react";
import { LayoutItem } from "../constants";
import { BugReportsStatsCard } from "../../BugReportsStatsCard";
import { ResizableSwappableCard } from "../ResizableSwappableCard";
import { 
  TotalUsersCard, 
  ContractsCard, 
  TotalValueCard, 
  EmailsSentCard 
} from "./StatCardWidgets";
import { UserGrowthChart } from "../UserGrowthChart";
import { WeeklyUserGrowthChart } from "../WeeklyUserGrowthChart";
import { ErrorDistributionChart } from "../ErrorDistributionChart";
import { RecentErrorsTable } from "../RecentErrorsTable";
import { ActiveUsersTable } from "../ActiveUsersTable";
import { NotableUsersCard } from "../NotableUsersCard";
import { EmailStatsChart } from "../EmailStatsChart";
import { InvitationsStatsCard } from "../InvitationsStatsCard";

export const useComponentMap = (
  stats: any,
  newUsersTimeline: any[],
  errorStats: any[],
  emailStats: any[],
  recentErrors: any[],
  activeUsers: any[],
  handlesVisible: boolean,
  onRemove?: (id: string) => void,
  onResize?: (id: string, newSize: 'small' | 'medium' | 'large' | 'xlarge') => void
) => {
  return useMemo(() => ({
    // Stat cards
    'total-users': (item: LayoutItem) => (
      <TotalUsersCard 
        item={item} 
        stats={stats} 
        handlesVisible={handlesVisible} 
        onRemove={onRemove} 
        onResize={onResize} 
      />
    ),
    'contracts': (item: LayoutItem) => (
      <ContractsCard 
        item={item} 
        stats={stats} 
        handlesVisible={handlesVisible} 
        onRemove={onRemove} 
        onResize={onResize} 
      />
    ),
    'admin-total-value': (item: LayoutItem) => (
      <TotalValueCard 
        item={item} 
        stats={stats} 
        handlesVisible={handlesVisible} 
        onRemove={onRemove} 
        onResize={onResize} 
      />
    ),
    'total-value': (item: LayoutItem) => (
      <TotalValueCard 
        item={item} 
        stats={stats} 
        handlesVisible={handlesVisible} 
        onRemove={onRemove} 
        onResize={onResize} 
      />
    ),
    'emails-sent': (item: LayoutItem) => (
      <EmailsSentCard 
        item={item} 
        stats={stats} 
        handlesVisible={handlesVisible} 
        onRemove={onRemove} 
        onResize={onResize} 
      />
    ),
    // Other widgets
    'user-growth': (item: LayoutItem) => (
      <UserGrowthChart 
        newUsersTimeline={newUsersTimeline}
        stats={{
          newUsersWeek: stats.newUsersWeek,
          newUsersMonth: stats.newUsersMonth,
          newUsersYear: stats.newUsersYear
        }}
        rearrangingEnabled={handlesVisible}
        onRemove={onRemove}
        size={item.size}
        onResize={onResize}
      />
    ),
    'error-distribution': (item: LayoutItem) => (
      <ErrorDistributionChart 
        errorStats={errorStats}
        totalErrorCount={stats.errorCount}
        rearrangingEnabled={handlesVisible}
        onRemove={onRemove}
        size={item.size}
        onResize={onResize}
      />
    ),
    'bug-reports': (item: LayoutItem) => (
      <ResizableSwappableCard 
        id="bug-reports" 
        size={item.size}
        showHandle={handlesVisible} 
        showResizeControl={handlesVisible}
        onRemove={handlesVisible ? onRemove : undefined}
        onResize={handlesVisible && onResize ? (newSize) => onResize("bug-reports", newSize) : undefined}
      >
        <BugReportsStatsCard />
      </ResizableSwappableCard>
    ),
    'recent-errors': (item: LayoutItem) => (
      <RecentErrorsTable 
        recentErrors={recentErrors}
        rearrangingEnabled={handlesVisible}
        onRemove={onRemove}
        size={item.size}
        onResize={onResize}
      />
    ),
    'active-users': (item: LayoutItem) => (
      <ActiveUsersTable 
        activeUsers={activeUsers}
        rearrangingEnabled={handlesVisible}
        onRemove={onRemove}
        size={item.size}
        onResize={onResize}
      />
    ),
    'notable-users': (item: LayoutItem) => (
      <NotableUsersCard 
        userWithMostContracts={stats.userWithMostContracts}
        userWithHighestValue={stats.userWithHighestValue}
        rearrangingEnabled={handlesVisible}
        onRemove={onRemove}
        size={item.size}
        onResize={onResize}
      />
    ),
    'email-statistics': (item: LayoutItem) => (
      <EmailStatsChart 
        emailStats={emailStats}
        rearrangingEnabled={handlesVisible}
        onRemove={onRemove}
        size={item.size}
        onResize={onResize}
      />
    ),
    'invitations-stats': (item: LayoutItem) => (
      <InvitationsStatsCard
        rearrangingEnabled={handlesVisible}
        onRemove={onRemove}
        size={item.size}
        onResize={onResize}
      />
    ),
    'weekly-user-growth': (item: LayoutItem) => (
      <WeeklyUserGrowthChart 
        weeklyUsersData={stats.weeklyUsersData || []}
        totalNewUsers={stats.totalNewUsers || 0}
        rearrangingEnabled={handlesVisible}
        onRemove={onRemove}
        size={item.size}
        onResize={onResize}
      />
    )
  }), [
    stats, 
    newUsersTimeline, 
    errorStats, 
    emailStats, 
    recentErrors, 
    activeUsers, 
    handlesVisible, 
    onRemove,
    onResize
  ]);
};
